import { useCallback, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import { fetchSystemPayments, formatDate, subscriptionStatusColor } from "../../utils/systemApi";
import { formatPriceMwk, getPlanById } from "../../config/subscriptionPlans";

export default function SubscriptionPayments() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [status, setStatus] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const data = await fetchSystemPayments({ status: status || undefined });
            setRows(Array.isArray(data) ? data : []);
        } catch (e) {
            setError(e.response?.data?.message || e.message || "Could not load payments");
        } finally {
            setLoading(false);
        }
    }, [status]);

    useEffect(() => {
        load();
    }, [load]);

    return (
        <Stack spacing={3}>
            <Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>Subscription payments</Typography>
                <Typography variant="body2" color="text.secondary">
                    All platform billing transactions from registered schools.
                </Typography>
            </Box>

            <FormControl size="small" sx={{ maxWidth: 200 }}>
                <InputLabel>Status</InputLabel>
                <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="Pending">Pending</MenuItem>
                    <MenuItem value="Completed">Completed</MenuItem>
                    <MenuItem value="Failed">Failed</MenuItem>
                </Select>
            </FormControl>

            {error && <Alert severity="error">{error}</Alert>}

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>School</TableCell>
                                <TableCell>Director</TableCell>
                                <TableCell>Reference</TableCell>
                                <TableCell>Plan</TableCell>
                                <TableCell>Amount</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Date</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row) => {
                                const schoolId = row.school?.id || row.school?._id || row.adminId;
                                return (
                                    <TableRow key={row.id || row._id} hover>
                                        <TableCell>
                                            {schoolId ? (
                                                <Typography
                                                    component={RouterLink}
                                                    to={`/System/schools/${schoolId}`}
                                                    sx={{ color: "primary.main", textDecoration: "none", fontWeight: 600 }}
                                                >
                                                    {row.school?.schoolName || row.schoolName || "—"}
                                                </Typography>
                                            ) : (
                                                row.school?.schoolName || row.schoolName || "—"
                                            )}
                                        </TableCell>
                                        <TableCell>{row.school?.name || "—"}</TableCell>
                                        <TableCell>{row.referenceNumber}</TableCell>
                                        <TableCell>{getPlanById(row.planId).name}</TableCell>
                                        <TableCell>{formatPriceMwk(row.amount)}</TableCell>
                                        <TableCell>
                                            <Chip size="small" color={subscriptionStatusColor(row.status)} label={row.status} />
                                        </TableCell>
                                        <TableCell>{formatDate(row.createdAt)}</TableCell>
                                    </TableRow>
                                );
                            })}
                            {!rows.length && (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                        No payments found.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Stack>
    );
}
